import { z } from 'zod';
import { compararDinheiro, somarDinheiro, subtrairDinheiro } from './aritmetica.ts';
import { dataSchema, dinheiroSchema, uuidSchema } from './comum.ts';
import {
  confrontoSchema,
  faixaDeVencimentoSchema,
  totalDeTitulosSchema,
  type FatiaDaFaixa,
  type TotalDeTitulos,
} from './painel-titulos.ts';

/**
 * Painel financeiro — as duas pontas do caixa na mesma mesa (`.spec/010`,
 * módulos Pagamento e Recebimento; `.spec/016`, seção 10).
 *
 * É **só leitura e só agregação**, como os dois painéis que ele compara: nenhum
 * título nasce, muda ou some por aqui, e nenhum saldo de conta corrente é
 * escrito. Ele soma o que os títulos, as baixas e as movimentações gravaram.
 *
 * ## Por que ele existe, se os dois painéis já existem
 *
 * Porque a pergunta de quem projeta o caixa não é "quanto se deve" nem "quanto
 * se tem a receber" — é **se o dinheiro chega**. Essa resposta não sai de duas
 * telas abertas lado a lado: exige subtrair uma faixa da outra, somar o saldo
 * que já está no banco e acumular o resultado ao longo do tempo. Feito à mão
 * numa planilha toda segunda-feira, é onde o erro entra.
 *
 * PAG·02 e REC·02 continuam existindo e continuam sendo mais fundos que este:
 * cada um abre a distribuição por conta contábil e por contraparte, que aqui
 * **não** têm equivalente. Fornecedor e cliente não se cruzam — são cadastros
 * diferentes —, e o plano de contas de despesa e o de receita não compartilham
 * uma única folha. Um gráfico "por conta contábil" comparando os dois lados
 * empilharia rótulos que nunca coincidem, e o que parece comparação seria só
 * duas listas coladas.
 *
 * ## O que se compara, então
 *
 * O que os dois lados **têm** em comum: a data de vencimento e o dinheiro. As
 * sete faixas de `painel-titulos.ts` são as mesmas para a dívida e para o
 * crédito — um título que vence em nove dias cai na mesma faixa dos dois lados
 * —, e é por isso que a comparação fecha.
 *
 * ## Sem filtro de obra, de propósito
 *
 * O título alcança a obra pelo rateio, e o **título sem rateio fica de fora** —
 * é o que PAG·02 e REC·02 avisam em caixa amarela. Num painel de comparação
 * isso seria pior do que um recorte incompleto: o pagar e o receber perderiam
 * linhas diferentes, e o líquido de cada faixa passaria a ser a diferença entre
 * dois recortes que não se correspondem. Pior ainda com o saldo de conta
 * corrente na conta: a conta corrente é da **empresa**, não da obra, e não há
 * como recortá-la sem inventar um critério.
 *
 * A obra tem painel próprio — CTR·03, em `painel-obras.ts` —, e ali a comparação
 * é entre custo e receita do canteiro: outra pergunta, com outro recorte. Lá o
 * título entra pela **fatia rateada** dele, e não inteiro, que é o que torna
 * legítimo somar obras diferentes sem contar o mesmo dinheiro duas vezes. Aqui
 * ele entra inteiro, porque a pergunta é sobre o caixa da empresa.
 */

/**
 * O recorte do painel: só a empresa.
 *
 * Ausente significa "tudo que o usuário alcança" — o recorte por empresa
 * vinculada continua sendo do servidor, como em toda listagem. É o filtro que a
 * gerência deixa vazio para ver o grupo inteiro, e o que o financeiro de uma
 * das empresas escolhe para ver a mesa dele.
 *
 * Escrito aqui, e não reaproveitado de um dos outros dois painéis, pelo mesmo
 * motivo que eles não compartilham o deles: a consulta é a fronteira pública
 * desta rota, e ela já **não** tem obra — copiar um schema com um campo a mais
 * abriria um filtro que este painel não sabe honrar.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const painelFinanceiroQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
  })
  .strict();

export type PainelFinanceiroQuery = z.infer<typeof painelFinanceiroQuerySchema>;

/* -------------------------------------------------------------------------- */
/* O dinheiro que já está no banco                                            */
/* -------------------------------------------------------------------------- */

/**
 * O saldo de uma conta corrente.
 *
 * Vem de `contas_correntes.saldo_atual`, que é **materializado**: quem o
 * mantém é a movimentação, na mesma transação que a grava, e o job noturno
 * `recalcula-saldos` o confere. Somar o razão aqui a cada abertura do painel
 * varreria o histórico inteiro do caixa para chegar ao mesmo número que já está
 * gravado — e para um número que discordaria do que o cadastro exibe se alguma
 * movimentação escapasse do recorte.
 *
 * A empresa vem junto porque sem ela duas contas chamadas "Caixa" em empresas
 * diferentes se leem como linha repetida — e o painel sem filtro de empresa é o
 * uso normal da tela.
 */
export const saldoDeContaSchema = z.object({
  id: uuidSchema,
  descricao: z.string(),
  /** Nome da empresa dona da conta. Nulo só se o vínculo não resolver. */
  empresaNome: z.string().nullable(),
  /** Pode ser **negativo**: conta em cheque especial existe. */
  saldo: dinheiroSchema,
});

export type SaldoDeConta = z.infer<typeof saldoDeContaSchema>;

/**
 * O caixa de hoje: o total e a lista que o compõe.
 *
 * **Só as contas ativas.** A conta encerrada não recebe lançamento novo, e o
 * saldo dela é resíduo de histórico — somá-lo à projeção seria contar dinheiro
 * que não está mais lá. É a mesma escolha do seletor de empresa do filtro.
 *
 * O total é a soma exata das linhas, calculada sobre elas e não perguntada de
 * novo ao banco: é o que permite à tela afirmar que a lista fecha com o
 * indicador. Uma segunda consulta somando tudo chegaria ao mesmo número com a
 * chance de chegar a um diferente.
 */
export const saldoEmContaSchema = z.object({
  total: dinheiroSchema,
  /** As contas ativas do recorte, da maior para a menor. */
  contas: z.array(saldoDeContaSchema),
});

export type SaldoEmConta = z.infer<typeof saldoEmContaSchema>;

/* -------------------------------------------------------------------------- */
/* A comparação, faixa a faixa                                                */
/* -------------------------------------------------------------------------- */

/*
  `confrontoSchema` e `confrontar` — os dois lados de um período e a subtração
  deles — **saíram** deste arquivo para `painel-titulos.ts` quando o painel de
  obras passou a confrontar os dois lados também. Lá a pergunta é se a obra se
  paga, aqui é se o caixa fecha, e a operação é literalmente a mesma.

  O que ficou é o confronto **por faixa**, abaixo, e ele tem um usuário só: o
  painel de obras compara os totais de um período que o usuário escolhe, e não a
  distância até hoje. Subir isto para o arquivo compartilhado criaria
  compartilhamento entre um usuário e nenhum.
*/

const ZERO = '0.00';

/**
 * Uma faixa de vencimento com os dois lados e o que sobra dela.
 *
 * `de` e `ate` vêm resolvidos do servidor, como nos outros dois painéis: a tela
 * não recalcula a janela num fuso diferente do de quem a decidiu.
 *
 * **`liquido` é `aReceber − aPagar`**, e é negativo na faixa em que se paga mais
 * do que se recebe — que é a informação inteira. Guardar o valor absoluto e um
 * sinal à parte obrigaria toda leitura a remontar a subtração.
 *
 * **`saldoProjetado` é acumulado**, e não o líquido da faixa: é o saldo em conta
 * mais os líquidos de todas as faixas até esta, inclusive. É o que responde à
 * pergunta que o painel existe para responder — **em que semana o caixa vira
 * negativo** —, e ela não se responde faixa a faixa isoladamente: uma faixa
 * negativa depois de três positivas pode não derrubar nada.
 */
export const fatiaComparadaSchema = z.object({
  faixa: faixaDeVencimentoSchema,
  de: dataSchema.nullable(),
  ate: dataSchema.nullable(),
  aPagar: totalDeTitulosSchema,
  aReceber: totalDeTitulosSchema,
  /** `aReceber − aPagar`. Negativo quando sai mais do que entra. */
  liquido: dinheiroSchema,
  /** Saldo em conta + os líquidos de todas as faixas até esta, inclusive. */
  saldoProjetado: dinheiroSchema,
});

export type FatiaComparada = z.infer<typeof fatiaComparadaSchema>;

/**
 * As duas séries de faixas, cruzadas, com a projeção acumulada.
 *
 * Recebe as séries **já completas** — as sete faixas, inclusive as zeradas —,
 * que é o que `serieDeFaixas` devolve. Cruzar séries incompletas exigiria
 * decidir aqui o que fazer com a faixa que existe de um lado e não do outro, e
 * a resposta certa (zero) já está tomada um passo antes, num lugar só.
 *
 * O cruzamento é **por faixa**, e não por posição, ainda que as duas venham da
 * mesma janela e na mesma ordem. É uma linha de código a mais e remove uma
 * suposição: o dia em que uma das séries chegar ordenada de outro jeito, o
 * líquido cruzaria a dívida da semana com o crédito do trimestre, e o número
 * resultante pareceria plausível.
 *
 * **O atraso entra na projeção, e entra primeiro.** É o que mais incomoda na
 * leitura — o painel começa contando com dinheiro que já deveria ter entrado —,
 * e é a leitura certa: o título vencido não deixou de existir, ele só está
 * atrasado, e tirá-lo da conta faria a projeção prometer um caixa que só se
 * realiza se ninguém pagar nem cobrar o que está vencido. A tela diz isso.
 */
export function serieComparada(
  aPagar: readonly FatiaDaFaixa[],
  aReceber: readonly FatiaDaFaixa[],
  saldoInicial: string,
): FatiaComparada[] {
  const receber = new Map(aReceber.map((fatia) => [fatia.faixa, fatia]));
  let acumulado = saldoInicial;

  return aPagar.map((paga) => {
    const recebida = receber.get(paga.faixa);
    const credito: TotalDeTitulos = recebida
      ? { quantidade: recebida.quantidade, valor: recebida.valor }
      : { quantidade: 0, valor: ZERO };

    const liquido = subtrairDinheiro(credito.valor, paga.valor);
    acumulado = somarDinheiro([acumulado, liquido]);

    return {
      faixa: paga.faixa,
      de: paga.de,
      ate: paga.ate,
      aPagar: { quantidade: paga.quantidade, valor: paga.valor },
      aReceber: credito,
      liquido,
      saldoProjetado: acumulado,
    };
  });
}

/* -------------------------------------------------------------------------- */
/* O que os contratos e as obras ainda prometem                               */
/* -------------------------------------------------------------------------- */

/**
 * O valor previsto em contratos e obras: o que o cadastro promete e o
 * financeiro ainda não recebeu como título.
 *
 * Cada documento entra por `valor − lançado`, **com piso em zero**: o contrato
 * em que se lançou a mais não reduz o previsto de outro — o excesso é dele, e
 * descontá-lo de um vizinho esconderia receita que ainda vai virar título.
 *
 * Quatro escolhas, todas do Adriano em 29/10/2026:
 *
 * - **Só contrato de recebimento**, ativo e com valor — o por medição não tem
 *   teto a prever.
 * - **A obra com contrato não soma por si**: o que ela lança abate o contrato
 *   dela. O orçamento gera contrato e obra com o mesmo total (decisão 59), e
 *   somar os dois contaria o mesmo dinheiro duas vezes. A obra sem contrato
 *   entra pelo próprio valor.
 * - **Lançado é título e nota de serviço.** O título com `contrato_id` ou
 *   `obra_id` — o gerado e o manual do aditivo — e a nota que faturou, pelo
 *   `valorFaturadoDaNotaServico`. O título da nota não aponta nem um nem outro
 *   (decisão 90), e por isso as duas somas não se sobrepõem.
 * - **Valor original do título, não o saldo**: a pergunta é o que ainda não
 *   foi lançado, e o recebido já foi.
 *
 * Fica **fora da projeção de caixa** de propósito: não tem vencimento, e somá-lo
 * às faixas inventaria uma data para dinheiro que nem título é ainda.
 */
export const previstoEmContratosEObrasSchema = z.object({
  valor: dinheiroSchema,
  /** Contratos com algo ainda a lançar. */
  contratos: z.number().int(),
  /** Obras sem contrato com algo ainda a lançar. */
  obras: z.number().int(),
});

export type PrevistoEmContratosEObras = z.infer<typeof previstoEmContratosEObrasSchema>;

/** Contrato ou obra sem contrato, pelo valor do cadastro. */
export interface DocumentoPrevisto {
  tipo: 'CONTRATO' | 'OBRA';
  id: string;
  valor: string;
}

/**
 * Um valor lançado no financeiro, com os vínculos que o apontam.
 *
 * `contratoDaObra` é o contrato da obra que o lançamento cita, lido por junção.
 */
export interface LancadoNoFinanceiro {
  contratoId: string | null;
  obraId: string | null;
  contratoDaObra: string | null;
  valor: string;
}

/**
 * A quem o lançamento abate: o contrato citado; sem ele, o da obra; sem os
 * dois, a obra. É a ordem da decisão 94 para a nota parcial.
 */
export function chaveDoLancamento(lancado: LancadoNoFinanceiro): string | null {
  const contrato = lancado.contratoId ?? lancado.contratoDaObra;
  if (contrato !== null) return `CONTRATO:${contrato}`;
  return lancado.obraId === null ? null : `OBRA:${lancado.obraId}`;
}

export function previstoEmContratosEObras(
  documentos: readonly DocumentoPrevisto[],
  lancados: readonly LancadoNoFinanceiro[],
): PrevistoEmContratosEObras {
  const porChave = new Map<string, string[]>();
  for (const lancado of lancados) {
    const chave = chaveDoLancamento(lancado);
    if (chave !== null) porChave.set(chave, [...(porChave.get(chave) ?? []), lancado.valor]);
  }

  const resultado = { valor: ZERO, contratos: 0, obras: 0 };
  for (const documento of documentos) {
    const lancado = somarDinheiro(porChave.get(`${documento.tipo}:${documento.id}`) ?? []);
    const resto = subtrairDinheiro(documento.valor, lancado);
    if (compararDinheiro(resto, ZERO) <= 0) continue;

    resultado.valor = somarDinheiro([resultado.valor, resto]);
    if (documento.tipo === 'CONTRATO') resultado.contratos += 1;
    else resultado.obras += 1;
  }

  return resultado;
}

/* -------------------------------------------------------------------------- */
/* A resposta                                                                 */
/* -------------------------------------------------------------------------- */

export const painelFinanceiroSchema = z.object({
  /** Hoje pelo relógio do servidor — o corte entre o atraso e o resto. */
  hoje: dataSchema,
  /** O dinheiro que já está no banco: o total e as contas ativas do recorte. */
  saldoEmConta: saldoEmContaSchema,
  /**
   * O que ainda vai acontecer, **sem recorte de data**.
   *
   * É a soma exata das sete faixas de cada lado, calculada sobre as mesmas
   * linhas — os dois números fecham com as barras por construção, e a tela pode
   * afirmar isso.
   */
  emAberto: confrontoSchema,
  /** As sete faixas cruzadas, na ordem do tempo, com a projeção acumulada. */
  porFaixa: z.array(fatiaComparadaSchema),
  /**
   * O saldo projetado depois da última faixa.
   *
   * É `saldoEmConta.total + emAberto.liquido`, e também o `saldoProjetado` da
   * última fatia — dois caminhos para o mesmo número, e é de propósito: ele é o
   * indicador de topo da tela, e lê-lo do fim de um array na apresentação é o
   * tipo de código que quebra em silêncio quando o array vem vazio.
   */
  saldoProjetado: dinheiroSchema,
  /** Primeiro dia da janela do realizado. */
  realizadoDe: dataSchema,
  /** Último dia da janela do realizado — hoje. */
  realizadoAte: dataSchema,
  /**
   * O que de fato saiu e entrou na janela: baixas não estornadas, pelo
   * **principal**. Juros, multa e desconto ficam de fora — eles não são do
   * título, e quem os quer tem o extrato da conta corrente (FIN·02).
   */
  realizado: confrontoSchema,
  /** O que contratos e obras ainda vão lançar no financeiro. Fora da projeção. */
  previstoEmContratosEObras: previstoEmContratosEObrasSchema,
});

export type PainelFinanceiro = z.infer<typeof painelFinanceiroSchema>;
